// ============================================================================
// Member metrics — guest loyalty, RFM segmentation, cohorts, LTV, churn.
// A "guest" is identified by memberId (preferred) or customerId. Accuracy is
// limited to the loaded reservation window; full-history accuracy needs a
// backend aggregate (see design doc §5.3).
// ============================================================================

import { daysBetween } from '../dateUtils';
import { bucketKey, bucketLabel, enumerateBuckets, groupBy, sumBy } from './aggregations';
import { AnalysisStay, Granularity, isRevenueStay } from './types';

export type GuestKey = string; // "m<id>" | "c<id>"

export function guestKey(stay: AnalysisStay): GuestKey | null {
  if (stay.memberId) return `m${stay.memberId}`;
  if (stay.customerId) return `c${stay.customerId}`;
  return null;
}

function revenueStaysByGuest(stays: AnalysisStay[]): Map<GuestKey, AnalysisStay[]> {
  const withGuest = stays.filter(isRevenueStay).filter(s => guestKey(s) !== null);
  return groupBy(withGuest, s => guestKey(s)!);
}

// ============================================================================
// Repeat vs new guests
// ============================================================================

export interface RepeatNewPoint {
  bucket: string;
  label: string;
  newGuests: number;
  repeatGuests: number;
}

/**
 * Per bucket, arrivals split into first-seen ("new") vs returning guests.
 * First-seen is relative to the whole loaded dataset, not the window.
 */
export function repeatVsNewSeries(
  stays: AnalysisStay[],
  from: string,
  to: string,
  granularity: Granularity
): RepeatNewPoint[] {
  const firstCheckIn = new Map<GuestKey, string>();
  for (const stay of stays.filter(isRevenueStay)) {
    const key = guestKey(stay);
    if (!key) continue;
    const existing = firstCheckIn.get(key);
    if (!existing || stay.checkIn < existing) firstCheckIn.set(key, stay.checkIn);
  }

  const buckets = enumerateBuckets(from, to, granularity);
  const result = new Map<string, { newGuests: number; repeatGuests: number }>(
    buckets.map(b => [b, { newGuests: 0, repeatGuests: 0 }])
  );
  for (const stay of stays.filter(isRevenueStay)) {
    const key = guestKey(stay);
    if (!key || stay.checkIn < from || stay.checkIn > to) continue;
    const bucket = result.get(bucketKey(stay.checkIn, granularity));
    if (!bucket) continue;
    if (firstCheckIn.get(key) === stay.checkIn) bucket.newGuests += 1;
    else bucket.repeatGuests += 1;
  }
  return buckets.map(key => ({
    bucket: key,
    label: bucketLabel(key, granularity),
    ...result.get(key)!,
  }));
}

/** Share of arrivals in the window from returning guests, as a percent. */
export function repeatGuestRate(stays: AnalysisStay[], from: string, to: string): number | null {
  const series = repeatVsNewSeries(stays, from, to, 'month');
  const repeat = sumBy(series, p => p.repeatGuests);
  const total = repeat + sumBy(series, p => p.newGuests);
  if (total === 0) return null;
  return Math.round((repeat / total) * 1000) / 10;
}

// ============================================================================
// RFM segmentation
// ============================================================================

export type RfmSegment = 'Champions' | 'Loyal' | 'New' | 'At risk' | 'Lapsed';

export interface GuestRfm {
  key: GuestKey;
  memberId?: number;
  customerId?: number;
  stays: number;
  revenue: number;
  lastCheckOut: string;
  recencyDays: number;
  segment: RfmSegment;
}

/**
 * Rule-based segments (quintile scoring is unstable at campground scale):
 *   Champions: 3+ stays, seen in the last 6 months
 *   Loyal:     2+ stays, seen in the last year
 *   New:       1 stay, seen in the last 6 months
 *   At risk:   2+ stays, not seen for over a year
 *   Lapsed:    everyone else
 */
export function rfmSegments(stays: AnalysisStay[], today: string): GuestRfm[] {
  const byGuest = revenueStaysByGuest(stays);
  const rows: GuestRfm[] = [];
  byGuest.forEach((guestStays, key) => {
    const lastCheckOut = guestStays.reduce((max, s) => (s.checkOut > max ? s.checkOut : max), guestStays[0].checkOut);
    const recencyDays = Math.max(0, daysBetween(lastCheckOut, today));
    const count = guestStays.length;
    let segment: RfmSegment;
    if (count >= 3 && recencyDays <= 180) segment = 'Champions';
    else if (count >= 2 && recencyDays <= 365) segment = 'Loyal';
    else if (count === 1 && recencyDays <= 180) segment = 'New';
    else if (count >= 2) segment = 'At risk';
    else segment = 'Lapsed';
    rows.push({
      key,
      memberId: guestStays[0].memberId,
      customerId: guestStays[0].customerId,
      stays: count,
      revenue: Math.round(sumBy(guestStays, s => s.cost) * 100) / 100,
      lastCheckOut,
      recencyDays,
      segment,
    });
  });
  return rows.sort((a, b) => b.revenue - a.revenue);
}

export const RFM_SEGMENT_ORDER: RfmSegment[] = ['Champions', 'Loyal', 'New', 'At risk', 'Lapsed'];

export function segmentSummary(guests: GuestRfm[]): Array<{ segment: RfmSegment; guests: number; revenue: number }> {
  return RFM_SEGMENT_ORDER.map(segment => {
    const rows = guests.filter(g => g.segment === segment);
    return { segment, guests: rows.length, revenue: sumBy(rows, g => g.revenue) };
  }).filter(row => row.guests > 0);
}

// ============================================================================
// Tenure cohorts
// ============================================================================

export interface MemberLite {
  id: number;
  name: string;
  createdAt?: string;
}

export interface TenureCohort {
  cohort: string; // signup year
  members: number;
  stays: number;
  revenue: number;
}

export function tenureCohorts(members: MemberLite[], stays: AnalysisStay[]): TenureCohort[] {
  const staysByMember = groupBy(
    stays.filter(isRevenueStay).filter(s => s.memberId),
    s => s.memberId!
  );
  const cohorts = new Map<string, TenureCohort>();
  for (const member of members) {
    if (!member.createdAt) continue;
    const year = member.createdAt.slice(0, 4);
    let cohort = cohorts.get(year);
    if (!cohort) {
      cohort = { cohort: year, members: 0, stays: 0, revenue: 0 };
      cohorts.set(year, cohort);
    }
    cohort.members += 1;
    const memberStays = staysByMember.get(member.id) ?? [];
    cohort.stays += memberStays.length;
    cohort.revenue += sumBy(memberStays, s => s.cost);
  }
  return Array.from(cohorts.values()).sort((a, b) => a.cohort.localeCompare(b.cohort));
}

// ============================================================================
// Churn risk
// ============================================================================

/**
 * Guests with a stay in the prior year but none in the last 12 months —
 * the "we lost them?" call list.
 */
export function churnRiskGuests(stays: AnalysisStay[], today: string): GuestRfm[] {
  return rfmSegments(stays, today).filter(
    g => g.segment === 'At risk' && g.recencyDays <= 730
  );
}

// ============================================================================
// Party composition
// ============================================================================

export interface PartyComposition {
  avgAdults: number;
  avgChildren: number;
  avgPets: number;
  avgVehicles: number;
  withChildrenPct: number;
  withPetsPct: number;
  stays: number;
}

export function partyComposition(stays: AnalysisStay[], from: string, to: string): PartyComposition {
  const rows = stays
    .filter(isRevenueStay)
    .filter(s => s.checkIn >= from && s.checkIn <= to);
  const n = rows.length;
  const avg = (f: (s: AnalysisStay) => number) => (n ? Math.round((sumBy(rows, f) / n) * 10) / 10 : 0);
  const pct = (f: (s: AnalysisStay) => boolean) => (n ? Math.round((rows.filter(f).length / n) * 1000) / 10 : 0);
  return {
    avgAdults: avg(s => s.adults),
    avgChildren: avg(s => s.children),
    avgPets: avg(s => s.pets),
    avgVehicles: avg(s => s.vehicles),
    withChildrenPct: pct(s => s.children > 0),
    withPetsPct: pct(s => s.pets > 0),
    stays: n,
  };
}
